import { MNSproCloudApi } from '..';
import {
  AnnouncementBasicResponse,
  AnnouncementCreate,
  AnnouncementResponse,
  AnnouncementUpdate,
  Area,
  EntityType,
  Recipients,
} from '../api/v2';

/**
 * Service class which defines methods to be used for MNSpro Cloud Organization management.
 *
 * @class Organization
 */
const AnnouncementsService = {
  /**
   *
   *
   * @static
   * @param {string} organizationId
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<AnnouncementResponse[]>}
   * @memberof AssignmentsService
   */
  async getAllAnnouncements(
    organizationId: string,
    authToken: string,
  ): Promise<AnnouncementResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.AnnouncementsApiFactory(config);

    const result = await api.getAllAnnouncements(organizationId);
    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} organizationId
   * @param {Area} AnnouncementArea
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<AnnouncementResponse[]>}
   * @memberof AssignmentsService
   */
  async getMyAnnouncements(
    authToken: string,
    organizationId: string,
    area: Area,
    groupId?: string,
    timePeriod?: string,
  ): Promise<AnnouncementBasicResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.AnnouncementsApiFactory(config);

    const result = await api.getMyAnnouncements(
      area,
      organizationId,
      timePeriod,
      groupId,
    );
    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} organizationId
   * @param {AnnouncementEntry} announcementEntry
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<AnnouncementResponse[]>}
   * @memberof AssignmentsService
   */
  async createAnnouncement(
    organizationId: string,
    announcementEntry: AnnouncementCreate,
    authToken: string,
  ): Promise<AnnouncementResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.AnnouncementsApiFactory(config);
    const result = await api.createAnnouncement(
      organizationId,
      announcementEntry,
    );
    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} organizationId
   * @param {AnnouncementEntry} announcementEntry
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<AnnouncementResponse[]>}
   * @memberof AssignmentsService
   */
  async updateAnnouncement(
    organizationId: string,
    announcementEntry: AnnouncementUpdate,
    authToken: string,
  ): Promise<AnnouncementResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.AnnouncementsApiFactory(config);
    const result = await api.updateAnnouncement(
      organizationId,
      announcementEntry,
    );
    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} organizationId
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @param {entityType} type
   * @return {*}  {Promise<AnnouncementResponse[]>}
   * @memberof AssignmentsService
   */
  async getPossibleAnnouncementRecipients(
    organizationId: string,
    type: EntityType,
    authToken: string,
  ): Promise<Recipients> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.AnnouncementsApiFactory(config);

    const result = await api.getRecipients(organizationId, undefined, type, {
      params: {
        // In order to make this API call to work in hybrid schools:
        // TimePeriod must be unset here, otherwise the timePeriod parameter will
        // be automatically appended in TimePeriodGuard and an empty response
        // will be returned in hybrid schools.
        timePeriod: '',
      },
    });
    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} organizationId
   * @param {AnnouncementEntry} announcementEntry
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<AnnouncementResponse[]>}
   * @memberof AssignmentsService
   */
  async deleteAnnouncement(
    organizationId: string,
    announcementId: string,
    authToken: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.AnnouncementsApiFactory(config);

    await api.deleteAnnouncement(announcementId, organizationId);
  },
};

export { AnnouncementsService };
